import { appendDistinctParameter } from './util/appendDistinctParameter';
import { appendDistinctValue } from './util/appendDistinctValue';
export class AnalysesManager {
    analyses;
    constructor() {
        this.analyses = [];
    }
    addAnalysis(analysis) {
        const index = this.getAnalysisIndex(analysis.id);
        if (index === undefined) {
            this.analyses.push(analysis);
        }
        else {
            this.analyses[index] = analysis;
        }
    }
    getAnalyses(options = {}) {
        const { ids } = options;
        const analyses = [];
        for (const analysis of this.analyses) {
            if (!ids || ids.includes(analysis.id)) {
                analyses.push(analysis);
            }
        }
        return analyses;
    }
    getMeasurements() {
        const measurements = [];
        for (const analysis of this.analyses) {
            measurements.push(...analysis.measurements);
        }
        return measurements;
    }
    getAnalysisByMeasurementId(id) {
        for (const analysis of this.analyses) {
            for (const measurement of analysis.measurements) {
                if (measurement.id === id)
                    return analysis;
            }
        }
        return undefined;
    }
    getMeasurementById(id) {
        for (const analysis of this.analyses) {
            for (const measurement of analysis.measurements) {
                if (measurement.id === id)
                    return measurement;
            }
        }
        return undefined;
    }
    /**
     * Get an array of objects (key + count) of all the titles.
     */
    getDistinctTitles() {
        const values = {};
        for (const measurement of this.getMeasurements()) {
            if (measurement.title) {
                appendDistinctValue(values, measurement.title);
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    /**
     * Get an array of objects (key + count) of all the units.
     */
    getDistinctUnits() {
        const values = {};
        for (const measurement of this.getMeasurements()) {
            if (measurement.variables) {
                for (const [, variable] of Object.entries(measurement.variables)) {
                    const units = variable.units?.replace(/\s+\[.*/, '');
                    if (units) {
                        appendDistinctValue(values, units);
                    }
                }
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    /**
     * Get an array of objects (key + unit + label + count) of all the units
     */
    getDistinctLabelUnits() {
        const values = {};
        for (const spectrum of this.getMeasurements()) {
            if (spectrum.variables) {
                for (const [, variable] of Object.entries(spectrum.variables)) {
                    const { label, units } = normalizeLabelUnits(variable.label, variable.units);
                    const key = label + (units ? ` (${units})` : '');
                    if (key) {
                        if (!values[key]) {
                            values[key] = { key, units, label, count: 0 };
                        }
                        values[key].count++;
                    }
                }
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    /**
     * Get an array of objects (key + count) of all the labels.
     */
    getDistinctLabels() {
        const values = {};
        for (const measurement of this.getMeasurements()) {
            if (measurement.variables) {
                for (const [, variable] of Object.entries(measurement.variables)) {
                    appendDistinctValue(values, variable.label.replace(/\s+\[.*/, ''));
                }
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    /**
     * Get an array of objects (key + count) of all the dataTypes.
     */
    getDistinctDataTypes() {
        const values = {};
        for (const measurement of this.getMeasurements()) {
            if (measurement.dataType) {
                appendDistinctValue(values, measurement.dataType);
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    /**
     * Get an array of objects (key + count) of all the meta.
     */
    getDistinctMeta() {
        const values = {};
        for (const measurement of this.getMeasurements()) {
            if (measurement.meta) {
                for (const key in measurement.meta) {
                    appendDistinctParameter(values, key, measurement.meta[key]);
                }
            }
        }
        return Object.keys(values).map((key) => values[key]);
    }
    removeAllAnalyses() {
        this.analyses.splice(0);
    }
    /**
     * Remove the analysis from the AnalysesManager for the specified id.
     * @param id
     */
    removeAnalysis(id) {
        const index = this.getAnalysisIndex(id);
        if (index === undefined)
            return undefined;
        return this.analyses.splice(index, 1);
    }
    /**
     * Returns the index of the analysis in the analyses array.
     * @param id
     */
    getAnalysisIndex(id) {
        if (!id)
            return undefined;
        for (let i = 0; i < this.analyses.length; i++) {
            const analysis = this.analyses[i];
            if (analysis.id === id)
                return i;
        }
        return undefined;
    }
    /**
     * Checks if the ID of an analysis exists in the AnalysesManager.
     * @param id
     */
    includes(id) {
        const index = this.getAnalysisIndex(id);
        return index === undefined ? false : !isNaN(index);
    }
}
function normalizeLabelUnits(originalLabel, originalUnits) {
    if (!originalLabel) {
        return { units: '', label: '' };
    }
    if (originalLabel.search(/[[(]]/) >= 0) {
        const [units, label] = originalLabel.split(/\s*[[(]/);
        return { units: originalUnits || units, label };
    }
    return { label: originalLabel, units: originalUnits };
}
//# sourceMappingURL=AnalysesManager.js.map